import { chatState } from "@/app/chatState";
import { EntryItem, useRefState } from "@/components/UI/tools";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { appState } from "@/app/appState";


function withIntentLabel(base: string, intent?: string): string {
    const trimmed = intent?.trim();
    return trimmed ? `${base} - ${trimmed}` : base;
}

const ChatTag = observer((params: { reload?: () => void, objKey: { type: "tag", id: number }, tool_id?: string, revision_id?: number, intent?: string }) => {

    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(0);

    const [name, setName] = useRefState<string>("");
    const [description, setDescription] = useRefState<string | undefined>("");
    const [scope, setScope] = useRefState<string | undefined>(undefined);
    const [permissionType, setPermissionType] = useRefState<number | undefined>(undefined);
    const [tprOrganisationIds, setTprOrganisationIds] = useRefState<number[]>([]);
    const [isDraft, setIsDraft] = useRefState<boolean | undefined>(undefined);
    const [lock, setLock] = useRefState<boolean>(false);

    const identity = (edit: boolean | number | undefined) => { }
    const [ids, setIds] = useState<number[]>([]);
    const [id, setId] = useState<number>();
    const [revId, setRevId] = useState<number>(-1);
    const [msgId, setMsgId] = useState<number>();

    const passDiffToMark = (difftext: string) => {
        if (!difftext) return;

        const lines = difftext.split('\n');
        let markdown = '';

        let currentType: 'add' | 'remove' | 'context' | null = null;
        let currentBlockLines: string[] = [];

        const flushBlock = () => {
            if (currentBlockLines.length === 0) return;

            const content = currentBlockLines.join('\n');
            if (currentType === 'add') {
                markdown += `<div class="[&_p]:!m-0" style="background-color: #0f02; width: fit-content; padding: 0.4rem;">\n\n${content}\n\n</div>\n`;
            } else if (currentType === 'remove') {
                markdown += `<div class="[&_p]:!m-0" style="background-color: #f002; width: fit-content; padding: 0.4rem;">\n\n${content}\n\n</div>\n`;
            } else {
                markdown += `<div class="[&_p]:!m-0" style="opacity: 0.6; width: fit-content; padding: 0.4rem;">\n\n${content}\n\n</div>\n`;
            }
            currentBlockLines = [];
            currentType = null;
        };

        for (const line of lines) {
            if (line.startsWith('---') || line.startsWith('++') || line.startsWith("\\")) {
                continue;
            }

            if (line.startsWith('@@')) {
                flushBlock();
                continue;
            }

            let type: 'add' | 'remove' | 'context' | null = null;
            let content = '';

            if (line.startsWith('-')) {
                type = 'remove';
                content = line.substring(1);
            } else if (line.startsWith('+')) {
                type = 'add';
                content = line.substring(1);
            } else if (line.startsWith(' ')) {
                type = 'context';
                content = line;
            } else {
                continue;
            }

            if (type !== currentType) {
                flushBlock();
                currentType = type;
            }

            currentBlockLines.push(content);
        }
        flushBlock();

        return markdown;
    }

    useEffect(() => {
        const obj = chatState.chatObjects.get(params.objKey)

        setName(obj?.tag?.name ?? "")

        if (!obj) {
            params.reload?.();
            return;
        }

        if (params.revision_id && obj.tag?.id) {
            const revisions_obj = [...obj?.revisions ?? []]?.sort((a, b) => a.id - b.id).filter(x => x?.id <= (params.revision_id ?? -1)).filter(x => x ? true : false) ?? []
            const revisions = revisions_obj.map(x => x?.description_diff)
            const { i, revObj } = revisions_obj?.map((x, i) => ({ i: i, revObj: x })).filter(x => x.revObj?.id == params.revision_id)[0] ?? { i: -1, revObj: null }
            setRevId(i)
            if (!revObj)
                return

            setDescription(passDiffToMark(revisions.at(-1) ?? ""))
            setScope(revObj.scope)
            setPermissionType(revObj.permission_type)
            setTprOrganisationIds((revObj.tpr_organisation_ids as number[]) ?? [])
            setLock(obj.tag.user_lock ? !(obj.tag.user_lock == appState.user?.id) : false)
            setId(obj.tag.id)
            setMsgId(obj.msgId)
            setIsDraft(obj.tag.is_draft)
        } else if ('description' in obj.tag) {
            setDescription(obj.tag.description)
            setScope(obj.tag.scope)
            setPermissionType(obj.tag.permission_type)
            setTprOrganisationIds((obj.tag.tpr_organisation_ids as number[]) ?? [])
            setLock(obj.tag.user_lock ? !(obj.tag.user_lock == appState.user?.id) : false)
            setId(obj.tag.id)
            setMsgId(obj.msgId)
            setIsDraft(obj.tag.is_draft)
        }

        setRefresh(p => p + 1)
    }, [params.objKey, chatState.chatObjects.get(params.objKey)])


    return (
        <div id={`tag${params.objKey.id}-${revId}`} title={`tag${params.objKey.id}-${revId}`} className={`h-full w-full`}>
            <EntryItem
                lock={lock.current}
                setEdit={() => { }}
                headerClickNavigateFunction={(() => {
                    if (id) {
                        const i = chatState.chatObjects.get({ type: "tag", id: id })?.revisions?.filter(x => x != undefined)?.findIndex(x => x?.id == params.revision_id) != -1
                            ?
                            chatState.chatObjects.get({ type: "tag", id: id })?.revisions?.filter(x => x != undefined)?.findIndex(x => x?.id == params.revision_id)
                            :
                            undefined

                        return () => chatState.openObject({ type: "tag", id }, i ?? -1);
                    }
                    return undefined
                })()}
                readonly={true}
                name={withIntentLabel((!params.revision_id) ? (isDraft.current ? appState.t("chat.tool.draftTag") : appState.t("objects.list.tag")) : (id && id > 0) ? appState.t("chat.tool.tagUpdate") : appState.t("chat.tool.entityOperation", { entity: appState.t("chat.objectType.tag"), operation: "create" }), params.intent)}
                chatForm={true}
                loading={loading}
                deleting={false}
                refreshCount={refresh}
                idParams={{
                    singular: { get: id, set: identity },
                    plural: { get: ids, set: setIds },
                }}
                refColumns={
                    params.revision_id && (description.current?.length ?? 0) > 0 ? [
                        {
                            type: "richtext",
                            label: appState.t("objects.description"),
                            validator: (value: string | undefined) => (value ? true : false),
                            value: description.current,
                            onChange: params.revision_id ? () => { } : setDescription,
                            artifactsComplete: true,
                        },
                    ]
                        :
                        undefined
                }
                nameColumn={{
                    type: "text",
                    value: name.current,
                    onChange: setName,
                    validator: (val: string) => /^[\p{L}0-9_\s-]+$/u.test(val),
                    label: "Name",
                }}
                stateColumns={[
                    {
                        label: appState.t("objects.scope"),
                        type: "switch",
                        onChange: (x) => {
                            const next = x === appState.t("objects.scope.me") || x === "me" ? "me"
                                : x === appState.t("objects.scope.organisation") || x === "organisation" ? "organisation"
                                : undefined;
                            setScope(next);
                        },
                        value: scope.current === "me" ? appState.t("objects.scope.me")
                            : scope.current === "organisation" ? appState.t("objects.scope.organisation")
                            : scope.current,
                        items: [{ name: appState.t("objects.scope.me") }, { name: appState.t("objects.scope.organisation") }],
                        validator: (val: string | undefined) => val ? true : false,
                    },
                    {
                        label: appState.t("objects.access"),
                        type: "single",
                        onChange: setPermissionType,
                        value: permissionType.current,
                        items: [{ id: 7, name: appState.t("objects.access.full") }, { id: 3, name: appState.t("objects.access.read") }, { id: 1, name: appState.t("objects.access.use") }],
                        validator: (val: number | undefined) => val ? true : false,
                    },
                    scope.current == "organisation" ?
                        {
                            label: appState.t("objects.shareTo"),
                            type: "multiple",
                            onChange: setTprOrganisationIds,
                            value: tprOrganisationIds.current,
                            items: appState.organisations?.filter(x => x.id != appState.user?.active_organisation.id).map((x) => {
                                return { id: x.id, name: x.name, defaultValue: tprOrganisationIds.current?.includes(x.id) };
                            }),
                            validator: (val: number[]) => true,
                        }
                        :
                        undefined
                ]}
            />
        </div>
    );
});


export default ChatTag